import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import "./MileageRatesSettings.css";
import { apiCall } from "../../api/apiCall";
import ConfirmDialog from "../../components/ConfirmDialog";
import RateFormModal from "./RateFormModal";
import { emptyRate } from "./utils";
import {
  EnergyType,
  MileageRate,
  MileageRateMode,
  MileageRateSettings as MileageRateSettingsType,
  SelectableEnergyType,
  VEHICLE_TYPES,
  VehicleType,
} from "../types";
import { getAuth, onAuthStateChanged } from "firebase/auth";

function MileageRatesSettings({
  mt,
  companyUuid,
  isSuperAdmin,
}: {
  mt: any;
  companyUuid: string | null | undefined;
  isSuperAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const mode = mt.mode ?? {};

  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [editingRate, setEditingRate] = useState<Partial<MileageRate> | null>(null);
  const [rateToDelete, setRateToDelete] = useState<MileageRate | null>(null);

  const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
    CAR: mt.vehicleTypes?.car ?? "Car",
    MOTORBIKE: mt.vehicleTypes?.motorbike ?? "Motorbike",
    MOPED: mt.vehicleTypes?.moped ?? "Moped",
  };

  const ENERGY_TYPE_LABELS: Record<SelectableEnergyType, string> = {
    THERMAL: mt.energyTypes?.thermal ?? "Thermal",
    ELECTRIC: mt.energyTypes?.electric ?? "Electric",
  };

  const getEnergyDisplayLabel = (energyType?: EnergyType | null): string => {
    if (!energyType) return mt.allEnergies ?? "All";
    if (energyType === "ELECTRIC") return ENERGY_TYPE_LABELS.ELECTRIC;
    return ENERGY_TYPE_LABELS.THERMAL;
  };

  const isGlobalNotacitoScope = isSuperAdmin && !companyUuid;

  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
        const auth = getAuth(); 
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          setIsAuthReady(true);
        });
        return () => unsubscribe();
      }, []);

  const {
    data: mileageRateSettings,
    isLoading: isLoadingMileageRateSettings,
  } = useQuery<MileageRateSettingsType>({
    queryKey: ["mileageRateSettings", companyUuid],
    queryFn: () =>
      apiCall<MileageRateSettingsType>(
        `/admin/companies/${companyUuid}/mileage-rate-settings`
      ),
    enabled: Boolean(companyUuid) && isAuthReady,
  });

  const isUsingNotacitoRates =
    Boolean(companyUuid) &&
    mileageRateSettings?.mileage_rate_mode === "NOTACITO_DEFAULT";

  const canEditMileageRates =
    isGlobalNotacitoScope ||
    (Boolean(companyUuid) &&
      !isLoadingMileageRateSettings &&
      mileageRateSettings?.mileage_rate_mode !== "NOTACITO_DEFAULT");

  const { data: mileageRates, isLoading: isLoadingRates } = useQuery<MileageRate[]>({
    queryKey: [
      "mileageRates",
      companyUuid,
      selectedYear,
      mileageRateSettings?.mileage_rate_mode,
    ],
    queryFn: () => {
      const queryParams = new URLSearchParams({ year: selectedYear.toString() });

      if (isGlobalNotacitoScope || isUsingNotacitoRates) {
        queryParams.append("company_uuid", "null");
      } else if (companyUuid) {
        queryParams.append("company_uuid", companyUuid);
      }

      return apiCall<MileageRate[]>(`/mileage-rates?${queryParams.toString()}`);
    },
    enabled: isGlobalNotacitoScope || (Boolean(companyUuid) && !isLoadingMileageRateSettings && isAuthReady),
  });

  // --- Mutations ---
  const updateMileageModeMutation = useMutation({
    mutationFn: (newMode: MileageRateMode) => {
      if (!companyUuid) {
        throw new Error(mode.noCompanySelected ?? "No company selected");
      }
      return apiCall<MileageRateSettingsType>(
        `/admin/companies/${companyUuid}/mileage-rate-settings`,
        { method: "PATCH", body: JSON.stringify({ mode: newMode }) }
      );
    },
    onSuccess: (_, newMode) => {
      toast.success(
        newMode === "NOTACITO_DEFAULT"
          ? mode.switchToNotacitoSuccess ?? "Notacito rates are now in use"
          : mode.switchToCustomSuccess ?? "Custom rates are now enabled"
      );
      setEditingRate(null);
      setRateToDelete(null);
      queryClient.invalidateQueries({ queryKey: ["mileageRateSettings", companyUuid] });
      queryClient.invalidateQueries({ queryKey: ["mileageRates"] });
    },
    onError: (error: Error) => {
      toast.error(
        (mode.switchError ?? "Failed to change rate mode: {message}").replace(
          "{message}",
          error.message
        )
      );
    },
  });

  const saveRateMutation = useMutation({
    mutationFn: (rate: Partial<MileageRate>) => {
      const isUpdate = Boolean(rate.uuid);
      const url = isUpdate ? `/mileage-rates/${rate.uuid}` : `/mileage-rates`;
      return apiCall(url, {
        method: isUpdate ? "PATCH" : "POST",
        body: JSON.stringify({
          ...rate,
          company_uuid: isSuperAdmin && !companyUuid ? null : companyUuid,
        }),
      });
    },
    onSuccess: () => {
      toast.success(mt.saveSuccess ?? "Rate saved successfully");
      setEditingRate(null);
      queryClient.invalidateQueries({ queryKey: ["mileageRates"] });
    },
    onError: (error: Error) => {
      toast.error((mt.saveError ?? "Failed to save: {message}").replace("{message}", error.message));
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (rate: MileageRate) =>
      apiCall(`/mileage-rates/${rate.uuid}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !rate.is_active }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mileageRates"] });
    },
    onError: (error: Error) => {
      toast.error(
        (mt.statusUpdateError ?? "Failed to update status: {message}").replace(
          "{message}",
          error.message
        )
      );
    },
  });

  const deleteRateMutation = useMutation({
    mutationFn: (uuid: string) => apiCall(`/mileage-rates/${uuid}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success(mt.deleteSuccess ?? "Rate deleted");
      setRateToDelete(null);
      queryClient.invalidateQueries({ queryKey: ["mileageRates"] });
    },
    onError: (error: Error) => {
      toast.error(
        (mt.deleteError ?? "Failed to delete: {message}").replace("{message}", error.message)
      );
      setRateToDelete(null);
    },
  });

  const handleMileageModeToggle = () => {
    if (!companyUuid || updateMileageModeMutation.isPending) return;
    updateMileageModeMutation.mutate(
      isUsingNotacitoRates ? "CUSTOM_WITH_FALLBACK" : "NOTACITO_DEFAULT"
    );
  };

  const groupedRates = useMemo(() => {
    const groups: Record<VehicleType, MileageRate[]> = { CAR: [], MOTORBIKE: [], MOPED: [] };
    (mileageRates ?? []).forEach((r) => groups[r.vehicle_type]?.push(r));
    (Object.keys(groups) as VehicleType[]).forEach((key) => {
      groups[key].sort(
        (a, b) => a.power_cv_min - b.power_cv_min || a.distance_min - b.distance_min
      );
    });
    return groups;
  }, [mileageRates]);

  const activeRatesCount = (mileageRates ?? []).filter((r) => r.is_active).length;
  const ratesCountText = (mt.ratesCountLabel ?? "rate(s) for {year}").replace(
    "{year}",
    String(selectedYear)
  );

  return (
    <section className="settings-card settings-card-wide">
      {companyUuid && (
        <div className="setting-group">
          <label>
            {isUsingNotacitoRates ? (
              <h2 className="settings-title settings-title-left">
                {mode.notacitoTitle ?? "Notacito rates"}
              </h2>
            ) : (
              <h2 className="settings-title settings-title-left">
                {mode.customTitle ?? "Custom rates"}
              </h2>
            )}
            <span>
              {isUsingNotacitoRates ? mode.notacitoDescription ?? "" : mode.customDescription ?? ""}
            </span>
          </label>

          <button
            type="button"
            className={
              isUsingNotacitoRates ? "save-settings-btn save-settings-btn-inline" : "btn-cancel"
            }
            onClick={handleMileageModeToggle}
            disabled={isLoadingMileageRateSettings || updateMileageModeMutation.isPending}
          >
            {updateMileageModeMutation.isPending
              ? mode.updating ?? "Updating..."
              : isUsingNotacitoRates
                ? mode.switchToCustom ?? "Customize rates"
                : mode.switchToNotacito ?? "Use Notacito rates"}
          </button>
        </div>
      )}

      <br /> {}

      <div className="mileage-rates-toolbar">
        <div>
          <h2 className="settings-title settings-title-left">{mt.title ?? "Mileage Rates"}</h2>
          <p className="settings-text settings-text-left">{mt.description ?? ""}</p>
        </div>

        <div className="mileage-rates-toolbar-actions">
          <div className="year-stepper">
            <button
              type="button"
              className="year-stepper-btn"
              onClick={() => setSelectedYear((y) => y - 1)}
            >
              ‹
            </button>
            <span className="year-stepper-value">{selectedYear}</span>
            <button
              type="button"
              className="year-stepper-btn"
              onClick={() => setSelectedYear((y) => y + 1)}
            >
              ›
            </button>
          </div>

          {canEditMileageRates && (
            <button
              type="button"
              className="save-settings-btn save-settings-btn-inline"
              onClick={() => setEditingRate(emptyRate(selectedYear))}
            >
              + {mt.addRate ?? "Add Rate"}
            </button>
          )}
        </div>
      </div>

      {!isLoadingRates && mileageRates && mileageRates.length > 0 && (
        <div className="mileage-summary-bar">
          <span>
            <strong>{mileageRates.length}</strong> {ratesCountText}
          </span>
          <span className="mileage-summary-dot">•</span>
          <span>
            <strong>{activeRatesCount}</strong> {mt.activeCountLabel ?? "active"}
          </span>
        </div>
      )}

      {isLoadingRates ? (
        <div className="rates-skeleton">
          <div className="skeleton-row" />
          <div className="skeleton-row" />
          <div className="skeleton-row" />
        </div>
      ) : mileageRates && mileageRates.length > 0 ? (
        <div className="mileage-rates-groups">
          {VEHICLE_TYPES.filter((vt) => groupedRates[vt].length > 0).map((vt) => (
            <div key={vt} className="rate-group">
              <h3 className="rate-group-title">{VEHICLE_TYPE_LABELS[vt]}</h3>
              <div className="rates-table-wrapper">
                <table className="rates-table">
                  <thead>
                    <tr>
                      <th>{mt.columnEnergy ?? "Energy"}</th>
                      <th>{mt.columnPower ?? "CV (min–max)"}</th>
                      <th>{mt.columnDistance ?? "Distance (km)"}</th>
                      <th>{mt.columnFixed ?? "Fixed"}</th>
                      <th>{mt.columnPerKm ?? "Per km"}</th>
                      <th>{mt.columnStatus ?? "Status"}</th>
                      <th>{mt.columnActions ?? ""}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedRates[vt].map((rate) => (
                      <tr key={rate.uuid} className={!rate.is_active ? "row-inactive" : ""}>
                        <td>{getEnergyDisplayLabel(rate.energy_type)}</td>
                        <td>
                          {rate.power_cv_min} – {rate.power_cv_max}
                        </td>
                        <td>
                          {rate.distance_min} – {rate.distance_max}
                        </td>
                        <td>{Number(rate.fixed_amount).toFixed(2)} €</td>
                        <td>{Number(rate.per_km_amount).toFixed(4)} €</td>
                        <td>
                          <button
                            type="button"
                            className={`status-badge ${rate.is_active ? "status-active" : "status-inactive"}`}
                            onClick={() => {
                              if (canEditMileageRates) toggleActiveMutation.mutate(rate);
                            }}
                            disabled={!canEditMileageRates || toggleActiveMutation.isPending}
                            title={
                              canEditMileageRates
                                ? mode.toggleTitleEditable ?? "Click to toggle status"
                                : mode.toggleTitleLocked ?? "Managed by Notacito"
                            }
                          >
                            {rate.is_active ? mt.statusActive ?? "Active" : mt.statusInactive ?? "Inactive"}
                          </button>
                        </td>
                        <td className="rate-actions-cell">
                          {canEditMileageRates ? (
                            <>
                              <button
                                type="button"
                                className="btn-edit"
                                onClick={() => setEditingRate(rate)}
                              >
                                {mt.edit ?? "Edit"}
                              </button>
                              <button
                                type="button"
                                className="btn-delete"
                                onClick={() => setRateToDelete(rate)}
                              >
                                {mt.delete ?? "Delete"}
                              </button>
                            </>
                          ) : (
                            <span className="status-badge status-inactive">
                              {mode.managedByNotacito ?? "Managed by Notacito"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rates-empty-state">
          <p>
            {isUsingNotacitoRates
              ? mt.noNotacitoRates ?? "No Notacito rates configured for this year."
              : mt.noCustomRates ?? "No custom rates configured for this year."}
          </p>
          {canEditMileageRates && (
            <button
              type="button"
              className="save-settings-btn save-settings-btn-inline"
              onClick={() => setEditingRate(emptyRate(selectedYear))}
            >
              + {mt.addRate ?? "Add Rate"}
            </button>
          )}
        </div>
      )}

      {editingRate && canEditMileageRates && (
        <RateFormModal
          initialRate={editingRate}
          isSaving={saveRateMutation.isPending}
          mt={mt}
          vehicleTypeLabels={VEHICLE_TYPE_LABELS}
          energyTypeLabels={ENERGY_TYPE_LABELS}
          onSubmit={(rate) => saveRateMutation.mutate(rate)}
          onClose={() => setEditingRate(null)}
        />
      )}

      {rateToDelete && canEditMileageRates && (
        <ConfirmDialog
          title={mt.deleteConfirmTitle ?? "Delete this rate?"}
          message={(
            mt.deleteConfirmMessage ??
            "The {vehicleType} rate ({powerMin}-{powerMax} CV) will be permanently deleted."
          )
            .replace("{vehicleType}", VEHICLE_TYPE_LABELS[rateToDelete.vehicle_type])
            .replace("{powerMin}", String(rateToDelete.power_cv_min))
            .replace("{powerMax}", String(rateToDelete.power_cv_max))}
          confirmLabel={mt.deleteConfirmBtn ?? "Delete"}
          cancelLabel={mt.cancel ?? "Cancel"}
          onConfirm={() => deleteRateMutation.mutate(rateToDelete.uuid!)}
          onCancel={() => setRateToDelete(null)}
        />
      )}
    </section>
  );
}

export default MileageRatesSettings;